/**
 * useGeolocation Hook
 *
 * Manages geolocation tracking with privacy-friendly location handling.
 * Includes fallback to IP-based geolocation when browser geolocation fails.
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import type { GeoLocation, GeofenceStatus } from '@/lib/types'
import { getGeofenceStatus } from '@/lib/geo'

export type LocationSource = 'gps' | 'ip' | 'manual'

export type LocationAccuracyMode = 'precise' | 'balanced' | 'quick'

export interface UseGeolocationOptions {
  enableHighAccuracy?: boolean
  watchPosition?: boolean
  timeout?: number
  maximumAge?: number
  enableIpFallback?: boolean
  accuracyMode?: LocationAccuracyMode
}

/**
 * Get geolocation options based on accuracy mode
 */
export function getOptionsForAccuracyMode(mode: LocationAccuracyMode): Partial<UseGeolocationOptions> {
  switch (mode) {
    case 'precise':
      return {
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 10000,
        enableIpFallback: true,
      }
    case 'balanced':
      return {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 30000,
        enableIpFallback: true,
      }
    case 'quick':
      return {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 60000,
        enableIpFallback: true,
      }
  }
}

export interface UseGeolocationReturn {
  location: GeoLocation | null
  error: GeolocationPositionError | null
  isLoading: boolean
  isWatching: boolean
  locationSource: LocationSource | null
  requestPermission: () => Promise<boolean>
  startWatching: () => void
  stopWatching: () => void
  setManualLocation: (location: GeoLocation) => void
  clearManualLocation: () => void
  getGeofenceStatus: (center: GeoLocation, radiusMeters: number) => GeofenceStatus | null
}

// IP Geolocation API (free, no key required)
async function fetchIpGeolocation(): Promise<GeoLocation | null> {
  // Try ipapi.co first (free, 1000 req/day, HTTPS)
  try {
    const response = await fetch('https://ipapi.co/json/')
    if (response.ok) {
      const data = await response.json()
      if (data.latitude && data.longitude) {
        console.log(`[Geolocation] IP fallback (ipapi.co) success: ${data.city}, ${data.country}`)
        return {
          latitude: data.latitude,
          longitude: data.longitude,
          accuracy: 5000, // City-level accuracy ~5km
          timestamp: new Date(),
        }
      }
    }
  } catch (e) {
    console.warn('[Geolocation] ipapi.co failed:', e)
  }

  // Fallback to ipwho.is (free, unlimited, HTTPS)
  try {
    const response = await fetch('https://ipwho.is/')
    if (response.ok) {
      const data = await response.json()
      if (data.success && data.latitude && data.longitude) {
        console.log(`[Geolocation] IP fallback (ipwho.is) success: ${data.city}, ${data.country}`)
        return {
          latitude: data.latitude,
          longitude: data.longitude,
          accuracy: 10000, // City-level accuracy
          timestamp: new Date(),
        }
      }
    }
  } catch (e) {
    console.warn('[Geolocation] ipwho.is failed:', e)
  }

  return null
}

export function useGeolocation(
  options: UseGeolocationOptions = {}
): UseGeolocationReturn {
  // If accuracyMode is provided, use its settings as defaults
  const modeOptions = options.accuracyMode
    ? getOptionsForAccuracyMode(options.accuracyMode)
    : {}

  const {
    enableHighAccuracy = modeOptions.enableHighAccuracy ?? true,
    watchPosition = false,
    timeout = modeOptions.timeout ?? 15000, // Increased timeout for slow GPS
    maximumAge = modeOptions.maximumAge ?? 30000, // Allow cached positions up to 30 seconds old
    enableIpFallback = modeOptions.enableIpFallback ?? true, // Enable IP-based fallback by default
  } = options

  const [location, setLocation] = useState<GeoLocation | null>(null)
  const [error, setError] = useState<GeolocationPositionError | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isWatching, setIsWatching] = useState(false)
  const [watchId, setWatchId] = useState<number | null>(null)
  const [locationSource, setLocationSource] = useState<LocationSource | null>(null)
  const [manualLocation, setManualLocationState] = useState<GeoLocation | null>(null)
  const retryCountRef = useRef(0)
  const maxRetries = 3
  const ipFallbackAttemptedRef = useRef(false)

  // Memoize geoOptions to prevent unnecessary re-renders
  const geoOptions: PositionOptions = useMemo(() => ({
    enableHighAccuracy,
    timeout,
    maximumAge,
  }), [enableHighAccuracy, timeout, maximumAge])

  // Lower accuracy options for fallback
  const lowAccuracyOptions: PositionOptions = useMemo(() => ({
    enableHighAccuracy: false,
    timeout: 20000,
    maximumAge: 60000, // Accept positions up to 1 minute old
  }), [])

  const handleSuccess = useCallback((position: GeolocationPosition) => {
    setLocation({
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      timestamp: new Date(position.timestamp),
    })
    setLocationSource('gps')
    setError(null)
    setIsLoading(false)
    retryCountRef.current = 0 // Reset retry count on success
    ipFallbackAttemptedRef.current = false // Reset IP fallback flag
  }, [])

  // Try IP-based geolocation as final fallback
  const tryIpFallback = useCallback(async () => {
    if (!enableIpFallback || ipFallbackAttemptedRef.current) {
      return false
    }

    ipFallbackAttemptedRef.current = true
    console.log('[Geolocation] Trying IP-based geolocation fallback...')

    const ipLocation = await fetchIpGeolocation()
    if (ipLocation) {
      setLocation(ipLocation)
      setLocationSource('ip')
      setError(null)
      setIsLoading(false)
      return true
    }

    return false
  }, [enableIpFallback])

  const handleError = useCallback(async (err: GeolocationPositionError) => {
    console.warn('[Geolocation] Error:', err.code, err.message)

    // On timeout or position unavailable, try with lower accuracy
    if ((err.code === 3 || err.code === 2) && retryCountRef.current < maxRetries) {
      retryCountRef.current++
      console.log(`[Geolocation] Retrying with lower accuracy (attempt ${retryCountRef.current}/${maxRetries})`)

      navigator.geolocation.getCurrentPosition(
        handleSuccess,
        async (retryErr) => {
          console.error('[Geolocation] Fallback also failed:', retryErr.message)

          // Try IP-based geolocation as final fallback
          const ipSuccess = await tryIpFallback()
          if (!ipSuccess) {
            setError(retryErr)
            setIsLoading(false)
          }
        },
        lowAccuracyOptions
      )
      return
    }

    // Try IP fallback immediately for permission denied
    const ipSuccess = await tryIpFallback()
    if (!ipSuccess) {
      setError(err)
      setIsLoading(false)
    }
  }, [handleSuccess, lowAccuracyOptions, tryIpFallback])

  const requestPermission = useCallback(async (): Promise<boolean> => {
    // If manual location is set, use that
    if (manualLocation) {
      setLocation(manualLocation)
      setLocationSource('manual')
      return true
    }

    if (!('geolocation' in navigator)) {
      // No browser geolocation, try IP fallback
      const ipSuccess = await tryIpFallback()
      return ipSuccess
    }

    setIsLoading(true)

    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          handleSuccess(position)
          resolve(true)
        },
        async (err) => {
          await handleError(err)
          // Resolve true if we got a location from IP fallback
          resolve(location !== null || locationSource === 'ip')
        },
        geoOptions
      )
    })
  }, [handleSuccess, handleError, geoOptions, manualLocation, tryIpFallback, location, locationSource])

  const startWatching = useCallback(() => {
    if (!('geolocation' in navigator)) {
      return
    }

    if (watchId !== null) {
      return // Already watching
    }

    setIsWatching(true)
    const id = navigator.geolocation.watchPosition(
      handleSuccess,
      handleError,
      geoOptions
    )
    setWatchId(id)
  }, [watchId, handleSuccess, handleError, geoOptions])

  const stopWatching = useCallback(() => {
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId)
      setWatchId(null)
      setIsWatching(false)
    }
  }, [watchId])

  // Manual location setters
  const setManualLocation = useCallback((loc: GeoLocation) => {
    setManualLocationState(loc)
    setLocation({
      ...loc,
      accuracy: loc.accuracy ?? 1, // Manual locations are precise
      timestamp: new Date(),
    })
    setLocationSource('manual')
    setError(null)
    setIsLoading(false)
    console.log('[Geolocation] Manual location set:', loc.latitude, loc.longitude)
  }, [])

  const clearManualLocation = useCallback(() => {
    setManualLocationState(null)
    setLocation(null)
    setLocationSource(null)
    ipFallbackAttemptedRef.current = false
    retryCountRef.current = 0
    console.log('[Geolocation] Manual location cleared')
  }, [])

  // Effective location (manual takes priority)
  const effectiveLocation = manualLocation ?? location

  const getGeofenceStatusFn = useCallback(
    (center: GeoLocation, radiusMeters: number): GeofenceStatus | null => {
      if (!effectiveLocation) return null
      return getGeofenceStatus(effectiveLocation, center, radiusMeters)
    },
    [effectiveLocation]
  )

  // Auto-start watching if requested
  useEffect(() => {
    if (watchPosition && !manualLocation) {
      startWatching()
    }

    return () => {
      if (watchId !== null) {
        navigator.geolocation.clearWatch(watchId)
      }
    }
  }, [watchPosition, manualLocation]) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    location: effectiveLocation,
    error,
    isLoading,
    isWatching,
    locationSource,
    requestPermission,
    startWatching,
    stopWatching,
    setManualLocation,
    clearManualLocation,
    getGeofenceStatus: getGeofenceStatusFn,
  }
}
